// Global keyboard shortcuts + the shortcuts overlay.
import { S, undo, redo, singleSelected, select } from './state.js';
import { $, h, kbdKeys } from './dom.js';
import * as A from './actions.js';
import * as V from './view.js';
import { openDialog, newBlank, save } from './io.js';
import { openCmdK, closeCmdK, isCmdKOpen } from './cmdk.js';
import { closeMenus } from './menus.js';
import { closeCheckPanel } from './check.js';

const SHORTCUTS = [
  { col: 'File', rows: [['Open', 'Mod+O'], ['Save', 'Mod+S'], ['Save as…', 'Mod+Shift+S'], ['New', 'Mod+Alt+N']] },
  {
    col: 'Edit',
    rows: [
      ['Undo', 'Mod+Z'], ['Redo', 'Mod+Shift+Z'], ['Duplicate', 'Mod+D'], ['Delete', 'Del'],
      ['Lock (editor only)', 'Mod+L'], ['Rotate 90°', 'R'], ['Bring to front', 'Mod+Shift+]'], ['Send to back', 'Mod+Shift+['],
    ],
  },
  {
    col: 'View',
    rows: [
      ['Fit to view', 'F'], ['Actual size', '1'], ['Toggle grid', 'G'], ['Zoom in / out', 'Mod+= / -'],
      ['Collapse fields panel', 'Mod+\\'], ['Collapse properties', 'Mod+/'],
    ],
  },
  {
    col: 'Selection',
    rows: [
      ['Select all', 'Mod+A'], ['Deselect', 'Esc'], ['Add to selection', 'Shift+click'], ['Marquee select', 'drag empty area'],
      ['Axis-lock drag', 'Shift+drag'], ['Duplicate while dragging', 'Alt+drag'], ['Nudge 0.1 mm', '↑↓←→'], ['Nudge 1 mm', 'Shift+↑↓←→'],
    ],
  },
  { col: 'Add', rows: [['Text', 'T'], ['Date', 'D'], ['Barcode', 'B']] },
  { col: 'Tools', rows: [['Command palette', 'Mod+K'], ['Shortcuts', '?'], ['Scrub a number', 'drag its label'], ['Scrub ×10 / ×0.1', 'Shift / Alt']] },
];

const renderKeys = (combo) => {
  if (/ /.test(combo) && !/\+/.test(combo.split(' ')[0])) return [h('kbd', null, combo)];
  return combo.split(' / ').flatMap((part, i) => [i ? ' / ' : null, ...kbdKeys(part).map((k) => h('kbd', null, k))]).filter(Boolean);
};

export function openShortcuts() {
  $('#shortcuts-grid').replaceChildren(
    ...SHORTCUTS.map((col) =>
      h(
        'div',
        { class: 'shortcuts-col' },
        h('h3', null, col.col),
        col.rows.map(([label, combo]) => h('div', { class: 'shortcut-row' }, h('span', { class: 'label' }, label), h('span', { class: 'keys' }, renderKeys(combo)))),
      ),
    ),
  );
  $('#shortcuts-overlay').classList.add('visible');
  setTimeout(() => $('#shortcuts-close').focus(), 20);
}
export const closeShortcuts = () => $('#shortcuts-overlay').classList.remove('visible');

function onKeyDown(e) {
  if (e.defaultPrevented) return;
  const t = e.target;
  const tag = (t && t.tagName) || '';
  const inForm = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable);
  const mod = e.metaKey || e.ctrlKey;
  const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
  const overlayOpen = document.querySelector('.overlay.visible');

  if (mod && k === 'k') {
    e.preventDefault();
    isCmdKOpen() ? closeCmdK() : openCmdK();
    return;
  }
  if (e.key === 'Escape') {
    const hadOverlay = overlayOpen || document.querySelector('.menu, .ctxmenu');
    closeCmdK();
    closeShortcuts();
    closeMenus();
    closeCheckPanel();
    $('#dpi-overlay').classList.remove('visible');
    if (inForm) t.blur();
    else if (!hadOverlay && S.selection.length) select([]);
    return;
  }
  if (overlayOpen) return; // dialogs own the keyboard

  if (mod) {
    if (k === 's') {
      e.preventDefault();
      if (S.label) save(e.shiftKey ? 'as' : 'save');
      return;
    }
    if (k === 'o') {
      e.preventDefault();
      openDialog();
      return;
    }
    if (k === 'n' && e.altKey) {
      e.preventDefault();
      newBlank();
      return;
    }
    if (k === '\\') {
      e.preventDefault();
      V.toggleLeft();
      return;
    }
    if (k === '/') {
      e.preventDefault();
      V.toggleRight();
      return;
    }
    if (k === '=' || k === '+') {
      e.preventDefault();
      V.zoomIn();
      return;
    }
    if (k === '-') {
      e.preventDefault();
      V.zoomOut();
      return;
    }
    if (k === '0') {
      e.preventDefault();
      V.fitToView();
      return;
    }
    if (inForm) return; // leave text editing shortcuts (undo, select all…) to the input
    if (k === 'z') {
      e.preventDefault();
      e.shiftKey ? redo() : undo();
      return;
    }
    if (k === 'y') {
      e.preventDefault();
      redo();
      return;
    }
    if (k === 'd') {
      e.preventDefault();
      A.duplicateSelected();
      return;
    }
    if (k === 'l') {
      e.preventDefault();
      A.toggleLockSelected();
      return;
    }
    if (k === 'a') {
      e.preventDefault();
      A.selectAll();
      return;
    }
    if ((e.code === 'BracketRight' || k === ']' || k === '}') && e.shiftKey) {
      e.preventDefault();
      A.bringToFront();
      return;
    }
    if ((e.code === 'BracketLeft' || k === '[' || k === '{') && e.shiftKey) {
      e.preventDefault();
      A.sendToBack();
    }
    return;
  }
  if (inForm || e.altKey) return;
  if (t && t.closest && t.closest('button, [role="button"], [role="menuitem"], [role="option"]') && (e.key === 'Enter' || e.key === ' ')) return;

  if (e.key === '?') {
    e.preventDefault();
    openShortcuts();
    return;
  }
  if (e.key === 'Delete' || e.key === 'Backspace') {
    if (S.selection.length) {
      e.preventDefault();
      A.deleteSelected();
    }
    return;
  }
  const arrows = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
  if (arrows[e.key]) {
    if (!S.selection.length) return;
    e.preventDefault();
    const step = e.shiftKey ? 100 : 10; // 1 mm / 0.1 mm
    A.nudge(arrows[e.key][0] * step, arrows[e.key][1] * step);
    return;
  }
  if (!S.label) return;
  if (k === 'f') V.fitToView();
  else if (k === '1') V.actualSize();
  else if (k === 'g') V.toggleGrid();
  else if (k === 't') A.addFieldOfKind('text');
  else if (k === 'd') A.addFieldOfKind('date');
  else if (k === 'b') A.addFieldOfKind('barcode');
  else if (k === 'r' && singleSelected() !== null) A.rotateSelected(90);
  else if (k === 'r' && S.selection.length) A.rotateSelected(90);
}

export function initShortcuts() {
  window.addEventListener('keydown', onKeyDown);
  $('#btn-help').onclick = openShortcuts;
  $('#shortcuts-close').onclick = closeShortcuts;
  $('#shortcuts-overlay').addEventListener('mousedown', (e) => {
    if (e.target.id === 'shortcuts-overlay') closeShortcuts();
  });
  $('#btn-undo').onclick = undo;
  $('#btn-redo').onclick = redo;
}
